import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Modal,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PhotoCameraBackIcon from "@mui/icons-material/PhotoCameraBack";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import GppMaybeIcon from "@mui/icons-material/GppMaybe";
import SearchIcon from "@mui/icons-material/Search";
import InfoIcon from "@mui/icons-material/Info";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import GoogleIcon from "@mui/icons-material/Google";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import { Autocomplete } from "@mui/material";
import styles from "./ConsentGalleryPanel.module.scss";

const ConsentGalleryPanel = ({ showGallery, onClose }) => {
  const [activeTab, setActiveTab] = useState("private");
  const [galleryData, setGalleryData] = useState({
    stock_gifs: [],
    stock_images: [],
    private: [],
  });
  const [categories, setCategories] = useState([]);
  const [categoryImagesMap, setCategoryImagesMap] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [filteredImages, setFilteredImages] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [gifSearchQuery, setGifSearchQuery] = useState("");
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(false);

  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isGoogleModalOpen, setIsGoogleModalOpen] = useState(false);
  const [showGifInfoModal, setShowGifInfoModal] = useState(false);
  const [showGoogleInfoModal, setShowGoogleInfoModal] = useState(false);

  const fetchGalleryData = async (search = "") => {
    try {
      const token = axios.defaults.headers.common.Authorization;
      if (!token) {
        toast.error("Authorization token is missing.");
        return;
      }

      setLoadingSearch(true);

      const response = await axios.get(
        `consent_clause/get_user_media?search=${encodeURIComponent(search)}`,
        {
          headers: {
            Authorization: `${token}`,
          },
        }
      );

      setGalleryData(
        response.data || {
          stock_gifs: [],
          stock_images: [],
          private: [],
        }
      );
    } catch (error) {
      console.error("Error fetching gallery data:", error);
      toast.error("Failed to load gallery data.");
    } finally {
      setLoadingSearch(false);
      setLoadingInitial(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data } = await axios.post(`/treatment_category`);
      setCategories(data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    if (!showGallery) return;

    setLoadingInitial(true);
    fetchGalleryData();
    fetchCategories();
  }, [showGallery]);

  useEffect(() => {
  if (!showGallery) return;
  fetchGalleryData();
}, [showGallery]);

  useEffect(() => {
    if (!categories.length || !galleryData) return;

    const newCategoryImagesMap = {};

    const categorizeItems = (items, type) => {
      items.forEach((item) => {
        const idsArray = Array.isArray(item.category_ids)
          ? item.category_ids
          : item.category_id != null
          ? [item.category_id]
          : [];

        idsArray.forEach((catId) => {
          const categoryName = categories.find(
            (category) => category.id === catId
          )?.category_name;

          if (categoryName) {
            if (!newCategoryImagesMap[categoryName]) {
              newCategoryImagesMap[categoryName] = {
                stock_gifs: [],
                private: [],
              };
            }

            if (type === "private") {
              newCategoryImagesMap[categoryName][type].push(item);
            } else if (!galleryData.private.some((p) => p.id === item.id)) {
              newCategoryImagesMap[categoryName][type].push(item);
            }
          }
        });
      });
    };

    categorizeItems(galleryData.stock_gifs || [], "stock_gifs");
    categorizeItems(galleryData.private || [], "private");

    Object.keys(newCategoryImagesMap).forEach((categoryName) => {
      const categoryData = newCategoryImagesMap[categoryName];
      const isEmpty =
        (!categoryData.stock_gifs || categoryData.stock_gifs.length === 0) &&
        (!categoryData.private || categoryData.private.length === 0);

      if (isEmpty) {
        delete newCategoryImagesMap[categoryName];
      }
    });

    setCategoryImagesMap(newCategoryImagesMap);
    setFilteredImages([]);
  }, [categories, galleryData]);

  const handleRefresh = () => {
    fetchGalleryData(searchQuery);
  };

  const handleCategoryClick = (categoryName) => {
    if (categoryName === "All") {
      setSelectedCategory(null);
      setFilteredImages([]);
      return;
    }

    if (selectedCategory === categoryName) {
      setSelectedCategory(null);
      setFilteredImages([]);
      return;
    }

    setSelectedCategory(categoryName);

    const imagesForCategory = [
      ...(activeTab === "stock_gifs"
        ? categoryImagesMap[categoryName]?.stock_gifs || []
        : []),
      ...(activeTab === "private"
        ? categoryImagesMap[categoryName]?.private || []
        : []),
    ];

    setFilteredImages(imagesForCategory);
  };

  const handleDeleteImage = async (mediaId) => {
    try {
      const token = axios.defaults.headers.common.Authorization;
      if (!token) {
        toast.error("Authorization token is missing.");
        return;
      }

      await axios.put(
        `/consent_clause/archive_user_media/${mediaId}`,
        {},
        {
          headers: {
            Authorization: token,
          },
        }
      );

      toast.success("Media archived successfully.");
      fetchGalleryData(searchQuery);
    } catch (error) {
      console.error("Error archiving media:", error);
      toast.error("Failed to archive media.");
    }
  };

  const fetchStockGifSearch = async (query = "") => {
    try {
      const token = axios.defaults.headers.common.Authorization;
      if (!token) return;

      setLoadingSearch(true);

      const response = await axios.get(
        `consent_clause/get_user_media?search=${encodeURIComponent(query)}`,
        {
          headers: { Authorization: token },
        }
      );

      setGalleryData((prev) => ({
        ...prev,
        stock_gifs: response.data?.stock_gifs || [],
      }));
    } catch (err) {
      console.error("GIF search failed", err);
      toast.error("GIF search failed.");
    } finally {
      setLoadingSearch(false);
    }
  };

  const visibleImages = useMemo(() => {
    return selectedCategory ? filteredImages : galleryData[activeTab] || [];
  }, [selectedCategory, filteredImages, galleryData, activeTab]);

  const visibleCategoryNames = useMemo(() => {
    return Object.keys(categoryImagesMap).filter((categoryName) => {
      const hasPrivateImages =
        (categoryImagesMap[categoryName]?.private || []).length > 0;
      const hasStockGifs =
        (categoryImagesMap[categoryName]?.stock_gifs || []).length > 0;

      return (
        (activeTab === "stock_gifs" && hasStockGifs) ||
        (activeTab === "private" && hasPrivateImages)
      );
    });
  }, [categoryImagesMap, activeTab]);

  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    return `${hrs.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  if (!showGallery) return null;

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.title}>Image Gallery</div>
          <div className={styles.subtitle}>
            Browse, upload, search Google, and reuse images
          </div>
        </div>

        <div className={styles.headerActions}>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Upload image">
            <IconButton
              onClick={() => setIsImageModalOpen(true)}
              size="small"
              className={styles.uploadIconBtn}
            >
              <PhotoCameraBackIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Close gallery">
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      <div className={styles.tabs}>
        {["private", "stock_gifs"].map((tab) => (
          <button
            key={tab}
            type="button"
            className={`${styles.tab} ${
              activeTab === tab ? styles.activeTab : ""
            }`}
            onClick={() => {
              setActiveTab(tab);
              setSelectedCategory(null);
              setFilteredImages([]);
            }}
          >
            {tab.replace("_", " ")}
          </button>
        ))}

        <Tooltip title="Search Google Images">
          <button
            type="button"
            className={styles.tab}
            onClick={() => setIsGoogleModalOpen(true)}
            aria-label="Search Google Images"
          >
            <GoogleIcon fontSize="small" />
          </button>
        </Tooltip>
      </div>

      {/* {activeTab === "private" && (
        <div className={styles.searchRow}>
          <div className={styles.searchContainer}>
            <div className={styles.searchBox}>
  <SearchIcon className={styles.searchIcon} />
  <input
    type="text"
    placeholder="Search Google images..."
    value={searchQuery}
    onChange={(e) => setSearchQuery(e.target.value)}
    onKeyDown={(e) => {
      if (e.key === "Enter" && searchQuery.trim()) {
        setIsGoogleModalOpen(true);
      }
    }}
  />
</div>

<Button
  className={styles.searchButton}
  onClick={() => setIsGoogleModalOpen(true)}
  disabled={!searchQuery.trim()}
>
  Search Web
</Button>

            <Tooltip title="About Google Image Search">
              <IconButton
                className={styles.infoButton}
                size="small"
                onClick={() => setShowGoogleInfoModal(true)}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      )} */}

      {activeTab === "stock_gifs" && (
        <div className={styles.searchRow}>
          <div className={styles.searchContainer}>
            <div className={styles.searchBox}>
              <SearchIcon className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search GIFs..."
                value={gifSearchQuery}
                onChange={(e) => setGifSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    fetchStockGifSearch(gifSearchQuery);
                  }
                }}
              />
            </div>

            <Button
              className={styles.searchButton}
              onClick={() => fetchStockGifSearch(gifSearchQuery)}
              disabled={!gifSearchQuery.trim()}
            >
              Search
            </Button>

            <Tooltip title="How to search GIFs">
              <IconButton
                className={styles.infoButton}
                size="small"
                onClick={() => setShowGifInfoModal(true)}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      )}

      <div className={styles.chipsRow}>
        {visibleCategoryNames.length > 0 && (
          <button
            type="button"
            className={`${styles.categoryChip} ${
              selectedCategory === null ? styles.selectedCategory : ""
            }`}
            onClick={() => handleCategoryClick("All")}
          >
            All
          </button>
        )}

        {visibleCategoryNames.map((categoryName) => (
          <button
            key={categoryName}
            type="button"
            className={`${styles.categoryChip} ${
              selectedCategory === categoryName ? styles.selectedCategory : ""
            }`}
            onClick={() => handleCategoryClick(categoryName)}
          >
            {categoryName}
          </button>
        ))}
      </div>

      <div className={styles.galleryBody}>
        {loadingInitial || loadingSearch ? (
          <div className={styles.loaderWrap}>
            <CircularProgress size={28} />
          </div>
        ) : visibleImages.length === 0 ? (
          <div className={styles.emptyState}>No images found.</div>
        ) : (
          <div className={styles.imageGrid}>
            {visibleImages.map((item) => {
              const keywords =
                typeof item.keywords === "string"
                  ? item.keywords
                      .split(",")
                      .map((keyword) => keyword.trim())
                      .filter(Boolean)
                  : [];

              return (
                <div key={item.id} className={styles.card}>
                  <div className={styles.imageWrap}>
                    <Tooltip title={keywords.join(", ") || item.name || "Image"}>
                      <img
                        src={item.url}
                        alt={item.name || "gallery-image"}
                        className={styles.image}
                      />
                    </Tooltip>

                    {item.duration && (
                      <div className={styles.durationBadge}>
                        {formatDuration(item.duration)}
                      </div>
                    )}

                    {item.is_confidential && (
                      <Tooltip title="Confidential">
                        <div className={styles.confidentialBadge}>
                          <GppMaybeIcon fontSize="small" />
                        </div>
                      </Tooltip>
                    )}
                  </div>

                  <div className={styles.cardFooter}>
                    <div
                      className={styles.imageName}
                      title={item.name || "Untitled"}
                    >
                      {item.name || "Untitled"}
                    </div>

                    {activeTab === "private" && (
                      <Tooltip title="Archive image">
                        <IconButton
                          size="small"
                          className={styles.deleteBtn}
                          onClick={() => handleDeleteImage(item.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <CompactImageUploadModal
        open={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        onUploadSuccess={() => {
          fetchGalleryData(searchQuery);
          setIsImageModalOpen(false);
        }}
      />

      <GoogleSearchModal
        open={isGoogleModalOpen}
        onClose={() => setIsGoogleModalOpen(false)}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onUploadSuccess={() => {
          fetchGalleryData(searchQuery);
          setIsGoogleModalOpen(false);
        }}
      />

      <Modal
        open={showGoogleInfoModal}
        onClose={() => setShowGoogleInfoModal(false)}
      >
        <Box className={styles.infoModalBox}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            About Google Image Search
          </Typography>

          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            Use Google image search to find images and import them directly into
            your private gallery.
            <br />
            <br />
            After selecting an image, you can assign:
            <br />• image name
            <br />• categories
            <br />• keywords
            <br />• confidential flag
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              onClick={() => setShowGoogleInfoModal(false)}
            >
              OK
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal open={showGifInfoModal} onClose={() => setShowGifInfoModal(false)}>
        <Box className={styles.infoModalBox}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            How to Search Stock GIFs
          </Typography>

          <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 2 }}>
            Search using short keywords related to dental procedures,
            treatments, symptoms, or conditions.
          </Typography>

          <ul style={{ lineHeight: 1.7, marginTop: 4 }}>
            <li>root canal</li>
            <li>crown prep</li>
            <li>tooth wear</li>
            <li>brushing</li>
            <li>gum disease</li>
          </ul>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Button variant="contained" onClick={() => setShowGifInfoModal(false)}>
              Got It
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

const CompactImageUploadModal = ({ open, onClose, onUploadSuccess }) => {
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isConfidential, setIsConfidential] = useState(false);
  const [imageName, setImageName] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [keywords, setKeywords] = useState("");
  const [keywordsArray, setKeywordsArray] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categoryError, setCategoryError] = useState(false);

  useEffect(() => {
    if (!open) return;

    const fetchCategories = async () => {
      try {
        const { data } = await axios.post(`/treatment_category`);
        setCategories(data || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, [open]);

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/jpeg": [],
      "image/png": [],
      "image/gif": [],
      "image/jpg": [],
    },
    multiple: false,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        setUploadedImages([acceptedFiles[0]]);
      }
    },
  });

  const handleKeywordAdd = (e) => {
    if (e.key === "Enter" && keywords.trim()) {
      e.preventDefault();
      const newKeyword = keywords.trim();
      if (!keywordsArray.includes(newKeyword)) {
        setKeywordsArray((prev) => [...prev, newKeyword]);
      }
      setKeywords("");
    }
  };

  const handleRemoveKeyword = (keyword) => {
    setKeywordsArray((prev) => prev.filter((kw) => kw !== keyword));
  };

  const removeImage = (index) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const resizeImage = (file, maxWidth, maxHeight) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = () => {
        img.onload = () => {
          let targetWidth = img.width;
          let targetHeight = img.height;

          if (img.width > maxWidth || img.height > maxHeight) {
            const widthRatio = maxWidth / img.width;
            const heightRatio = maxHeight / img.height;
            const scale = Math.min(widthRatio, heightRatio);
            targetWidth = img.width * scale;
            targetHeight = img.height * scale;
          }

          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            reject("Canvas context not found");
            return;
          }

          canvas.width = targetWidth;
          canvas.height = targetHeight;
          ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject("Failed to create blob");
                return;
              }
              const resizedFile = new File([blob], file.name, {
                type: file.type,
              });
              resolve(resizedFile);
            },
            file.type,
            0.9
          );
        };

        img.src = reader.result;
      };

      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async () => {
  if (uploadedImages.length === 0) {
    toast.error("No images selected.");
    return;
  }

  setLoading(true);

  try {
    const token = axios.defaults.headers.common.Authorization;

    for (const file of uploadedImages) {
      const resizedFile = await resizeImage(file, 300, 202);
      const formData = new FormData();

      formData.append("file", resizedFile);
      formData.append("is_confidential", isConfidential ? "true" : "false");

      if (imageName.trim()) {
        formData.append("user_file_name", imageName.trim());
      }

      if (keywordsArray.length > 0) {
        formData.append("keywords", keywordsArray);
      }

      selectedCategories.forEach((idOrObj) => {
        const id = typeof idOrObj === "object" ? idOrObj.id : idOrObj;
        if (id != null) {
          formData.append("category_ids", String(id));
        }
      });

      await axios.post("consent_clause/upload_user_media_to_s3", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: token,
        },
      });
    }

    toast.success("Content added to the image gallery.");
    onUploadSuccess?.();

    setUploadedImages([]);
    setIsConfidential(false);
    setImageName("");
    setSelectedCategories([]);
    setKeywords("");
    setKeywordsArray([]);
  } catch (error) {
    console.error("Error uploading image:", error);
    toast.error("Failed to upload image.");
  } finally {
    setLoading(false);
  }
};

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.uploadModal}>
        <div className={styles.uploadModalHeader}>
          <Typography variant="h6" fontWeight={700}>
            Upload Image
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>

        <div {...getRootProps()} className={styles.dropZone}>
          <input {...getInputProps()} />
          <p>Drag and drop an image here</p>
          <span>or click to select a file</span>
        </div>

        {uploadedImages.length > 0 && (
          <div className={styles.previewList}>
            {uploadedImages.map((file, index) => (
              <div key={index} className={styles.previewCard}>
                <img
                  src={URL.createObjectURL(file)}
                  alt="Preview"
                  className={styles.previewImage}
                />
                <Button
                  size="small"
                  color="error"
                  onClick={() => removeImage(index)}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}

        

        <label className={styles.confidentialRow}>
          <input
            type="checkbox"
            checked={isConfidential}
            onChange={(e) => setIsConfidential(e.target.checked)}
          />
          <span>Mark as confidential</span>
        </label>

        <div className={styles.uploadActionRow}>
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleImageUpload}
            disabled={loading}
          >
            {loading ? "Uploading..." : "Upload"}
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

const GoogleSearchModal = ({
  open,
  onClose,
  searchQuery,
  setSearchQuery,
  onUploadSuccess,
}) => {
  const apiKey = "AIzaSyDBGKDuYYvI1DRbCjvrtnCRasnGukhYOes";
  const cx = "30dca0bba2d8b4b27";
  const numResults = 10;

  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasSearched, setHasSearched] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(0);
  const [selectedGoogleImage, setSelectedGoogleImage] = useState(null);

  const [keywords, setKeywords] = useState("");
  const [keywordsArray, setKeywordsArray] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categoryError, setCategoryError] = useState(false);
  const [isConfidential, setIsConfidential] = useState(false);
  const [imageName, setImageName] = useState("");
  const [imageNameError, setImageNameError] = useState(false);

  const [previewImage, setPreviewImage] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);

  const fetchResults = async (page, shouldKeepOpen = true) => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    setImagesLoaded(0);

    try {
      const startIndex = (page - 1) * numResults + 1;
      const rightsFilter =
        "cc_publicdomain|cc_attribute|cc_commercial|cc_nonderived";

      const apiUrl = `https://www.googleapis.com/customsearch/v1?q=${encodeURIComponent(
        searchQuery
      )}&cx=${cx}&searchType=image&key=${apiKey}&num=${numResults}&start=${startIndex}&rights=${rightsFilter}`;

      const response = await fetch(apiUrl);
      const data = await response.json();

      const results = (data.items || []).filter((item) => {
        const link = item.link?.toLowerCase();
        return (
          link &&
          (link.endsWith(".jpg") ||
            link.endsWith(".jpeg") ||
            link.endsWith(".png"))
        );
      });

      setSearchResults(results);
      if (!shouldKeepOpen) return;
    } catch (error) {
      console.error("Error fetching search results:", error);
      setSearchResults([]);
      toast.error("Search failed.");
    }
  };

  useEffect(() => {
    if (!open) return;

    const fetchCategories = async () => {
      try {
        const { data } = await axios.post(`/treatment_category`);
        setCategories(data || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, [open]);

    useEffect(() => {
    if (!open) return;

    if (searchQuery.trim() && !hasSearched) {
        setHasSearched(true);
        setCurrentPage(1);
        fetchResults(1);
        return;
    }

    if (hasSearched) {
        fetchResults(currentPage);
    }
    }, [open, currentPage, hasSearched, searchQuery]);

  useEffect(() => {
    if (searchResults.length > 0 && imagesLoaded === searchResults.length) {
      setLoading(false);
    }
  }, [imagesLoaded, searchResults]);

  useEffect(() => {
    const handleCloseContext = () => setContextMenu(null);
    window.addEventListener("click", handleCloseContext);
    window.addEventListener("scroll", handleCloseContext);

    return () => {
      window.removeEventListener("click", handleCloseContext);
      window.removeEventListener("scroll", handleCloseContext);
    };
  }, []);

  const handleSearch = () => {
    if (!searchQuery.trim()) return;
    setCurrentPage(1);
    setHasSearched(true);
    fetchResults(1);
  };

  const handleClear = () => {
    setSearchQuery("");
    setSearchResults([]);
    setCurrentPage(1);
    setHasSearched(false);
    setSelectedGoogleImage(null);
  };

  const handleNextPage = () => {
    setCurrentPage((prevPage) => prevPage + 1);
  };

  const handlePrevPage = () => {
    setCurrentPage((prevPage) => (prevPage > 1 ? prevPage - 1 : 1));
  };

  const handleKeywordAdd = (e) => {
    if (e.key === "Enter" && keywords.trim()) {
      e.preventDefault();
      const newKeyword = keywords.trim();
      if (!keywordsArray.includes(newKeyword)) {
        setKeywordsArray([...keywordsArray, newKeyword]);
      }
      setKeywords("");
    }
  };

  const handleRemoveKeyword = (keywordToRemove) => {
    setKeywordsArray(keywordsArray.filter((k) => k !== keywordToRemove));
  };

  const handleImageLoad = () => {
    setImagesLoaded((prev) => prev + 1);
  };

  const handleImageSearchUpload = async () => {
  if (!selectedGoogleImage) {
    toast.error("No image selected.");
    return;
  }

  setLoading(true);

  try {
    const token = axios.defaults.headers.common.Authorization;

    const formData = new FormData();
    formData.append("image_url", selectedGoogleImage);
    formData.append("is_confidential", isConfidential ? "true" : "false");

    await axios.post("consent_clause/upload_google_image_to_s3", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: token,
      },
    });

    toast.success("Content added to the image gallery.");

    setSelectedGoogleImage(null);
    setIsConfidential(false);
    onUploadSuccess?.();
    onClose();
  } catch (error) {
    console.error("Error uploading image URL:", error);
    toast.error("Failed to upload image.");
  } finally {
    setLoading(false);
  }
};

  const closeModal = () => {
  setSearchResults([]);
  setCurrentPage(1);
  setHasSearched(false);
  setSelectedGoogleImage(null);
  setIsConfidential(false);
  setPreviewImage(null);
  setContextMenu(null);
  onClose();
};
  return (
    <>
      {previewImage && (
        <div className={styles.previewOverlay} onClick={() => setPreviewImage(null)}>
          <div
            className={styles.previewBox}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setPreviewImage(null)}
              className={styles.previewCloseBtn}
            >
              &times;
            </button>
            <img src={previewImage} alt="Preview" className={styles.previewLargeImage} />
          </div>
        </div>
      )}

      <Modal open={open} onClose={closeModal}>
        <div className={styles.googleModalOverlay}>
          <div className={styles.googleModalContent}>
            <button onClick={closeModal} className={styles.googleModalCloseBtn}>
              &times;
            </button>

            {/* <p className={styles.googleHelpText}>
              Right-click an image to preview it or open it in a new tab
            </p> */}

            {/* <h2 className={styles.googleModalTitle}>
              Search Results for <strong>{searchQuery}</strong> (Page {currentPage})
            </h2> */}

            <Box marginBottom={3} display="flex" justifyContent="center">
              <input
                type="text"
                placeholder="Search Google Images..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.googleSearchTopInput}
              />

              <button onClick={handleSearch} className={styles.googlePrimaryBtn}>
                Search
              </button>

              {searchQuery && (
                <button onClick={handleClear} className={styles.googleSecondaryBtn}>
                  Clear
                </button>
              )}
            </Box>

            <div className={styles.googleResultGrid}>
              {searchResults.length === 0 && !loading && <p>No results found.</p>}

              {searchResults.map((result, index) => (
                <div
                  key={index}
                  className={styles.googleResultCard}
                >
                  <img
                    src={result.link}
                    alt="Image result"
                    onClick={() => setSelectedGoogleImage(result.link)}
                    onLoad={handleImageLoad}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      setContextMenu({
                        x: e.pageX,
                        y: e.pageY,
                        imageUrl: result.link,
                      });
                    }}
                    className={`${styles.googleResultImage} ${
                      selectedGoogleImage === result.link
                        ? styles.googleResultSelected
                        : ""
                    }`}
                  />

                  {selectedGoogleImage === result.link && (
                    <div className={styles.googleSelectedBadge}>
                      <CheckBoxIcon style={{ fontSize: 28 }} />
                    </div>
                  )}
                </div>
              ))}

              {contextMenu && (
                <div
                  className={styles.contextMenu}
                  style={{
                    top: contextMenu.y,
                    left: contextMenu.x,
                  }}
                  onContextMenu={(e) => e.preventDefault()}
                >
                  <div
                    className={styles.contextMenuItem}
                    onClick={() => {
                      setPreviewImage(contextMenu.imageUrl);
                      setContextMenu(null);
                    }}
                  >
                    Preview
                  </div>
                  <div
                    className={styles.contextMenuItem}
                    onClick={() => {
                      window.open(contextMenu.imageUrl, "_blank");
                      setContextMenu(null);
                    }}
                  >
                    Open in new tab
                  </div>
                </div>
              )}
            </div>

            {searchResults.length > 0 && (
              <div className={styles.googlePagination}>
                <Button onClick={handlePrevPage}>Previous</Button>
                <Button onClick={handleNextPage}>Next</Button>
              </div>
            )}

            <div className={styles.googleUploadForm}>
  <div style={{ marginBottom: "20px" }}>
    <label style={{ fontWeight: 500 }}>
      <input
        type="checkbox"
        checked={isConfidential}
        onChange={(e) => setIsConfidential(e.target.checked)}
        style={{ marginRight: "8px" }}
      />
      Mark this image as <strong>confidential</strong>
    </label>
  </div>

  <button
    onClick={handleImageSearchUpload}
    className={styles.googleUploadBtn}
    disabled={loading || !selectedGoogleImage}
  >
    {loading ? "Uploading..." : "Upload Selected Image"}
  </button>
</div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ConsentGalleryPanel;
